/** `app` log label for this deployable (02-architecture §2, 07-logging §labels). */
export const APP_NAME = 'web-client'

export interface AppEnv {
  apiBaseUrl: string
  environment: string
  /** Grafana Faro collector URL; empty means telemetry is off. */
  faroUrl: string | null
  /**
   * DSN проекта dorixona-client в Sentry (sentry.udevs.io); пусто — ошибки не
   * отправляются. Задан в `.env` (прод), в `.env.staging` намеренно пуст.
   */
  sentryDsn: string | null
  /** Браузерный ключ Яндекс Карт (JS API 2.1); пусто — карта скажет, что её нет. */
  yandexMapsKey: string
  /**
   * Ключ «API Геокодера» Яндекса: улица и дом по точке и поиск адреса. Если у
   * геокодера нет своего ключа, берём ключ карт — продукты можно привязать к одному.
   */
  yandexGeocoderKey: string
  /**
   * Показывать пометки «нет в API» прямо на экране. Это рабочий инструмент
   * команды, а не сообщение покупателю: в продовой сборке он выключен, а на
   * стенде и в разработке включён, чтобы незакрытые места были видны сразу.
   */
  showApiGaps: boolean
  /**
   * Номер аккаунта для проверки в магазинах приложений (R-02a, на сервере —
   * `REVIEW_PHONE`), только цифры. Рецензенту не показываем онлайн-оплату:
   * платить ему нечем, а проверку она только запутывает. Пусто — таких нет.
   */
  reviewPhone: string
  /** Новые функции за флагами `VITE_FEATURE_<ИМЯ>` (docs/conventions.md). */
  features: {
    /** Сторис на главной, ведутся из админки (`VITE_FEATURE_STORIES`). */
    stories: boolean
    /** Новости: блок на главной, список и экран новости (`VITE_FEATURE_NEWS`). */
    news: boolean
    /** Отзывы об аптеке после заказа (`VITE_FEATURE_REVIEWS`). */
    reviews: boolean
    /** Избранные лекарства (`VITE_FEATURE_FAVORITES`). */
    favorites: boolean
    /** Мультипоиск лекарств в каталоге (`VITE_FEATURE_MULTISEARCH`). */
    multiSearch: boolean
    /**
     * Ключ Яндекс Карт только из пула на сервере (`GET /maps/key`); пул пуст —
     * карта показывает ошибку (`VITE_FEATURE_MAPS_KEYS`).
     */
    mapsKeys: boolean
    /** Push по токену в нашем Flutter-приложении (`VITE_FEATURE_PUSH`). */
    push: boolean
  }
  /**
   * Сторис из mock-хранилища в браузере вместо API — только для показа без
   * бэкенда. Ручки есть (`/stories`, `/admin/stories`): по умолчанию API,
   * mock — лишь при `'true'`.
   */
  storiesMock: boolean
  /** Новости из mock-хранилища в браузере — только при `'true'`; по умолчанию API. */
  newsMock: boolean
  /** Отзывы из mock-хранилища в браузере — только при `'true'`; по умолчанию API. */
  reviewsMock: boolean
  /** Избранное из mock-хранилища в браузере — только при `'true'`; по умолчанию API. */
  favoritesMock: boolean
}

/** The subset of import.meta.env this app reads; tests pass plain objects. */
export interface EnvSource {
  readonly VITE_API_BASE_URL?: string
  readonly VITE_ENV?: string
  readonly VITE_FARO_URL?: string
  readonly VITE_SENTRY_DSN?: string
  readonly VITE_YANDEX_MAPS_KEY?: string
  readonly VITE_YANDEX_GEOCODER_KEY?: string
  readonly VITE_SHOW_API_GAPS?: string
  readonly VITE_REVIEW_PHONE?: string
  readonly VITE_FEATURE_STORIES?: string
  readonly VITE_STORIES_MOCK?: string
  readonly VITE_FEATURE_NEWS?: string
  readonly VITE_NEWS_MOCK?: string
  readonly VITE_FEATURE_REVIEWS?: string
  readonly VITE_FEATURE_FAVORITES?: string
  readonly VITE_FEATURE_MULTISEARCH?: string
  readonly VITE_FEATURE_PUSH?: string
  readonly VITE_FEATURE_MAPS_KEYS?: string
  readonly VITE_REVIEWS_MOCK?: string
  readonly VITE_FAVORITES_MOCK?: string
  readonly DEV?: boolean
}

/**
 * Флаг новой функции `VITE_FEATURE_<ИМЯ>`: включена только значением `'true'`.
 * Пусто, опечатка или переменной нет — выключена, так что функция не попадёт
 * к пользователям, пока её явно не включат в `.env` своего стенда.
 */
export function featureFlag(value: string | undefined): boolean {
  return value === 'true'
}

export function readEnv(source: EnvSource): AppEnv {
  return {
    apiBaseUrl: source.VITE_API_BASE_URL ?? '/api/v1',
    environment: source.VITE_ENV ?? 'local',
    faroUrl: source.VITE_FARO_URL ? source.VITE_FARO_URL : null,
    sentryDsn: source.VITE_SENTRY_DSN ? source.VITE_SENTRY_DSN : null,
    yandexMapsKey: source.VITE_YANDEX_MAPS_KEY ?? '',
    yandexGeocoderKey: source.VITE_YANDEX_GEOCODER_KEY || (source.VITE_YANDEX_MAPS_KEY ?? ''),
    // Явный флаг главнее: им пометки включают на стенде и выключают в проде.
    showApiGaps: source.VITE_SHOW_API_GAPS
      ? source.VITE_SHOW_API_GAPS === 'true'
      : (source.DEV ?? false),
    reviewPhone: (source.VITE_REVIEW_PHONE ?? '').replace(/\D/g, ''),
    features: {
      stories: featureFlag(source.VITE_FEATURE_STORIES),
      news: featureFlag(source.VITE_FEATURE_NEWS),
      reviews: featureFlag(source.VITE_FEATURE_REVIEWS),
      favorites: featureFlag(source.VITE_FEATURE_FAVORITES),
      multiSearch: featureFlag(source.VITE_FEATURE_MULTISEARCH),
      push: featureFlag(source.VITE_FEATURE_PUSH),
      mapsKeys: featureFlag(source.VITE_FEATURE_MAPS_KEYS),
    },
    storiesMock: source.VITE_STORIES_MOCK === 'true',
    newsMock: source.VITE_NEWS_MOCK === 'true',
    reviewsMock: source.VITE_REVIEWS_MOCK === 'true',
    favoritesMock: source.VITE_FAVORITES_MOCK === 'true',
  }
}

export const env: AppEnv = readEnv(import.meta.env)
